import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Checkbox,
  Grid,
  Card,
  CardContent,
  Chip,
  Divider,
  Button,
  Alert,
  Fade,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SettingsIcon from "@mui/icons-material/Settings";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import { useWizard } from "../WizardContext";

// Import shared preview component mapping
import {
  blockPreviewComponents,
  PreviewRenderer,
} from "../utils/PreviewComponentMapping";

// Legacy mapping for backward compatibility
const previewComponents = {
  hero: blockPreviewComponents.hero,
  PHCG: blockPreviewComponents.phcg,
  bookPromo: blockPreviewComponents.bookPromo,
  threeSteps: blockPreviewComponents.threeSteps,
  testimonials: blockPreviewComponents.testimonials,
  assistanceLevels: blockPreviewComponents.assistanceLevels,
  charter: blockPreviewComponents.charterSection,
  charterTypes: blockPreviewComponents.charterTypes,
  dypAdvantages: blockPreviewComponents.dypAdvantages,
  dypLicensedUser: blockPreviewComponents.dypLicensedUser,
  dypTool: blockPreviewComponents.dypTool,
  pcProcess: blockPreviewComponents.pcProcess,
  partners: blockPreviewComponents.partnersBenefits,
  sliversOfAmbiguity: blockPreviewComponents.slivers,
};

// Get descriptive text for each block
const getBlockDescription = (blockId) => {
  switch (blockId) {
    case "hero":
      return "An eye-catching header block introducing the Partnership Charter";
    case "PHCG":
      return "About your consulting company and partnership expertise";
    case "charter":
      return "Explains what a Partnership Charter is and why it's important";
    case "charterTypes":
      return "Describes the types of partners who can benefit from a Charter";
    case "threeSteps":
      return "Visual guide showing the 3-step PC process";
    case "dypTool":
      return "Introduction to the Design Your Partnership tool";
    case "partners":
      return "Benefits and advantages of Partnership Charters";
    case "pcProcess":
      return "The 13 critical topics covered in the Workbook";
    case "dypAdvantages":
      return "Advantages, besides being the only tool for designing partnerships!";
    case "assistanceLevels":
      return "The two different levels of support available to partners";
    case "bookPromo":
      return "The PC book helps many partners understand and want to develop a Charter";
    case "testimonials":
      return "PC client success stories you’re free to use";
    case "sliversOfAmbiguity":
      return "How developing a PC addresses partners’ “pain points”";
    case "dypLicensedUser":
      return "Your firm is licensed to use the DYP to help partners who find your website";
    default:
      return "Professional content block for your website";
  }
};

// Default configurations for preview
const getDefaultConfig = (blockId) => {
  switch (blockId) {
    case "hero":
      return {
        backgroundImageUrl: "https://tpci.vercel.app/hero_bg.png",
        overlayColor: "rgba(28, 67, 106, 0.7)",
        textColor: "#ffffff",
        headingFont: "serif",
        taglineText: "...never operate a partnership without one",
        logoUrl: "https://tpci.vercel.app/phcg_logo.png",
      };
    case "PHCG":
      return {
        backgroundColor: "#1C436A",
        textColor: "#ffffff",
        headingFont: "serif",
        bodyFont: "sans-serif",
        buttonColor: "#CE950F",
        buttonText: null, // Will use dynamic text based on company name/acronym
        buttonUrl: "https://PHCG-associates.com",
        companyName: "Post-Haste Consulting Group",
        companyAcronym: "PHCG",
      };
    case "bookPromo":
      return {
        backgroundColor: "#CE950F",
        textColor: "#ffffff",
        headingFont: "serif",
        bodyFont: "sans-serif",
        borderRadius: 16,
        bookImageSrc: "https://tpci.vercel.app/book_partnership_charter.png",
        contactLink: "https://example.com/contact",
        barnesNobleLogo: "https://tpci.vercel.app/logo_barnes_noble.png",
        googlePlayLogo: "https://tpci.vercel.app/logo_google_play.png",
        amazonLogo: "https://tpci.vercel.app/logo_amazon.png",
        indieBoundLogo: "https://tpci.vercel.app/logo_indie_bound.png",
        audibleLogo: "https://tpci.vercel.app/logo_audible.png",
        invertLogos: false,
        marginTop: "20px",
        marginBottom: "20px",
      };
    case "threeSteps":
      return {
        backgroundColor: "#ffffff",
        textColor: "#333333",
        headingColor: "#1C436A",
        primaryColor: "#1C436A",
        accentColor: "#CE950F",
        headingFont: "serif",
        bodyFont: "sans-serif",
        discoverIconSrc: "https://tpci.vercel.app/step1.png",
        discussIconSrc: "https://tpci.vercel.app/step2.png",
        documentIconSrc: "https://tpci.vercel.app/step3.png",
      };
    case "testimonials":
      return {
        backgroundColor: "#ffffff",
        textColor: "#333333",
        headingFont: "serif",
        bodyFont: "sans-serif",
        accentColor: "#1C436A",
      };
    case "dypTool":
      return {
        backgroundColor: "#ffffff",
        textColor: "#333333",
        headingFont: "serif",
        bodyFont: "sans-serif",
        imageSrc: "https://tpci.vercel.app/dyp_tool.png",
        imageRadius: 8,
      };
    case "assistanceLevels":
      return {
        backgroundColor: "#ffffff",
        textColor: "#333333",
        headingFont: "serif",
        bodyFont: "sans-serif",
        imageSrc: "https://tpci.vercel.app/assistance_levels_diagram.png",
      };
    case "charter":
      return {
        backgroundColor: "#ffffff",
        textColor: "#333333",
        headingFont: "serif",
        bodyFont: "sans-serif",
        imageSrc: "https://tpci.vercel.app/chartersection_computer.jpg",
      };
    case "charterTypes":
      return {
        backgroundColor: "#ffffff",
        textColor: "#333333",
        headingFont: "serif",
        bodyFont: "sans-serif",
        primaryColor: "#1C436A",
      };
    case "dypAdvantages":
      return {
        backgroundColor: "#1C436A",
        textColor: "#ffffff",
        headingColor: "#ffffff",
        headingFont: "serif",
        bodyFont: "sans-serif",
        primaryColor: "#1C436A",
        accentColor: "#CE950F",
        accordionRadius: 8,
        transitionSpeed: 0.3,
        buttonColor: "#CE950F",
        buttonRadius: 8,
        buttonText: null, // Will use dynamic text based on company acronym
        companyAcronym: "PHCG",
      };
    case "dypLicensedUser":
      return {
        backgroundColor: "#ffffff",
        textColor: "#333333",
        headingFont: "serif",
        bodyFont: "sans-serif",
        primaryColor: "#1C436A",
        buttonText: null, // Will use dynamic text
        organizationName: null, // Will use dynamic company acronym
        companyAcronym: "PHCG",
      };
    case "pcProcess":
      return {
        backgroundColor: "#ffffff",
        textColor: "#333333",
        headingFont: "serif",
        bodyFont: "sans-serif",
        primaryColor: "#1C436A",
      };
    case "partners":
      return {
        backgroundColor: "#ffffff",
        textColor: "#333333",
        headingFont: "serif",
        bodyFont: "sans-serif",
        primaryColor: "#1C436A",
      };
    case "sliversOfAmbiguity":
      return {
        backgroundColor: "#ffffff",
        textColor: "#333333",
        headingFont: "serif",
        bodyFont: "sans-serif",
        primaryColor: "#1C436A",
      };
    default:
      return {
        backgroundColor: "#ffffff",
        textColor: "#333333",
        headingFont: "serif",
        bodyFont: "sans-serif",
        borderRadius: 8,
        primaryColor: "#1C436A",
      };
  }
};

/**
 * Step 1: Block Selection Component
 * Displays blocks on the left with checkboxes in the middle and live preview on the right
 */
const BlockSelector = () => {
  const {
    availableBlocks,
    selectedBlocks,
    toggleBlock,
    blockConfigs,
    setCurrentStep,
  } = useWizard();
  const [isMobile, setIsMobile] = useState(false);
  const [showMobileAlert, setShowMobileAlert] = useState(false);

  // Check for mobile device on component mount
  useEffect(() => {
    const checkMobile = () => {
      const mobile =
        window.innerWidth < 768 ||
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          navigator.userAgent
        );
      setIsMobile(mobile);
      setShowMobileAlert(mobile);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  return (
    <Box sx={{ minHeight: "60vh" }}>
      {/* Mobile Warning Alert */}
      {isMobile && (
        <Fade in={isMobile}>
          <Alert
            severity="warning"
            icon={<PhoneAndroidIcon />}
            sx={{
              mb: 3,
              borderRadius: 2,
              "& .MuiAlert-message": { width: "100%" },
            }}
          >
            <Typography variant="body2" fontWeight="bold" gutterBottom>
              Desktop Required for Block Selection
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              The block selection interface requires a larger screen. Please use
              a desktop or tablet to continue.
            </Typography>
            <Button
              variant="contained"
              size="small"
              sx={{
                bgcolor: "#CE950F",
                "&:hover": { bgcolor: "#B5850D" },
              }}
              onClick={() => setCurrentStep(0)}
            >
              Back to Introduction
            </Button>
          </Alert>
        </Fade>
      )}

      {/* Column Headers with Visual Flow */}
      {!isMobile && (
        <Grid container spacing={0} sx={{ mb: 1, px: 0 }}>
          <Grid item xs={12} md={7} sx={{ order: { xs: 1, md: 3 } }}>
            <Box
              sx={{
                textAlign: "center",
                p: 1,
                bgcolor: "#f8f9fa",
                borderRadius: 1,
                mx: 0,
              }}
            >
              <Typography
                variant="h6"
                fontWeight="bold"
                sx={{ color: "#1C436A", mb: 0.5 }}
              >
                Preview your PC website page
              </Typography>
              <Typography variant="caption" color="text.secondary">
                These are the blocks that will appear on your site
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={1} sx={{ order: { xs: 2, md: 2 } }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "flex-start",
                pt: 1,
                px: 0,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: "#1C436A",
                  fontSize: "0.75rem",
                  mb: 1,
                  fontWeight: "500",
                }}
              >
                Select
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Checkbox
                  checked={selectedBlocks.length === availableBlocks.length}
                  indeterminate={
                    selectedBlocks.length > 0 &&
                    selectedBlocks.length < availableBlocks.length
                  }
                  onChange={() => {
                    if (selectedBlocks.length === availableBlocks.length) {
                      // Deselect all
                      selectedBlocks.forEach((block) => toggleBlock(block.id));
                    } else {
                      // Select all
                      availableBlocks.forEach((block) => {
                        if (!block.selected) toggleBlock(block.id);
                      });
                    }
                  }}
                  sx={{
                    color: "#1C436A",
                    "&.Mui-checked": { color: "#CE950F" },
                    "&.MuiCheckbox-indeterminate": { color: "#CE950F" },
                    "& .MuiSvgIcon-root": { fontSize: 28 },
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    color: "#1C436A",
                    fontSize: "0.75rem",
                    fontWeight: "500",
                  }}
                >
                  ALL
                </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={4} sx={{ order: { xs: 3, md: 1 } }}>
            <Box
              sx={{
                textAlign: "center",
                p: 1,
                bgcolor: "#f8f9fa",
                borderRadius: 1,
                mx: 0,
              }}
            >
              <Typography
                variant="h6"
                fontWeight="bold"
                sx={{ color: "#1C436A", mb: 0.5 }}
              >
                Available blocks
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Select the blocks you’d like for your website
              </Typography>
            </Box>
          </Grid>
        </Grid>
      )}

      {/* Unified scrollable content with perfect alignment */}
      {!isMobile && (
        <Paper
          elevation={2}
          sx={{
            borderRadius: 2,
            maxHeight: "80vh",
            overflow: "auto",
          }}
        >
          <Box sx={{ p: 0 }}>
            {availableBlocks.map((block, index) => (
              <AlignedBlockRow
                key={block.id}
                block={block}
                isSelected={block.selected}
                onToggle={() => toggleBlock(block.id)}
                config={blockConfigs[block.id] || getDefaultConfig(block.id)}
                isLast={index === availableBlocks.length - 1}
              />
            ))}
          </Box>
        </Paper>
      )}
    </Box>
  );
};

/**
 * Aligned block row component - shows block preview, checkbox, and live preview in perfect horizontal alignment
 */
const AlignedBlockRow = ({ block, isSelected, onToggle, config, isLast }) => {
  const PreviewComponent = blockPreviewComponents[block.id];
  const [isHovered, setIsHovered] = useState(false);

  return (
    <Grid
      container
      spacing={0}
      sx={{
        mb: 0,
        alignItems: "flex-start",
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Left Column: Live Website Preview */}
      <Grid item xs={12} md={7} sx={{ order: { xs: 1, md: 3 } }}>
        {isSelected && PreviewComponent ? (
          <Box
            sx={{
              width: "100%",
              "& > *": {
                margin: 0,
                width: "100% !important",
                maxWidth: "100% !important",
                boxSizing: "border-box !important",
              },
            }}
          >
            <PreviewComponent config={config} />
          </Box>
        ) : null}
      </Grid>

      {/* Middle Column: Checkbox */}
      <Grid item xs={12} md={1} sx={{ order: { xs: 2, md: 2 } }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "flex-start",
            width: "100%",
            height: "100%",
            position: "relative",
          }}
        >
          {/* Top border line */}
          <Box
            sx={{
              width: "60%",
              height: "1px",
              bgcolor: "#e0e0e0",
              opacity: 0.5,
            }}
          />

          {/* Checkbox in center */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Checkbox
              checked={isSelected}
              onChange={onToggle}
              sx={{
                color: "#1C436A",
                "&.Mui-checked": {
                  color: "#CE950F",
                  animation: isSelected
                    ? "checkboxPulse 0.3s ease-out"
                    : "none",
                },
                "& .MuiSvgIcon-root": {
                  fontSize: 28,
                  filter: isHovered
                    ? "drop-shadow(0 2px 4px rgba(0,0,0,0.2))"
                    : "none",
                },
                "@keyframes checkboxPulse": {
                  "0%": { transform: "scale(1)" },
                  "50%": { transform: "scale(1.2)" },
                  "100%": { transform: "scale(1)" },
                },
              }}
            />
          </Box>

          {/* Bottom border line */}
          <Box
            sx={{
              width: "60%",
              height: "1px",
              bgcolor: "#e0e0e0",
              opacity: 0.5,
            }}
          />
        </Box>
      </Grid>

      {/* Right Column: Block Preview with Label */}
      <Grid item xs={12} md={4} sx={{ order: { xs: 3, md: 1 } }}>
        <Box
          sx={{
            border: isSelected
              ? "2px solid #CE950F"
              : isHovered
              ? "2px solid #f5e6a3"
              : "1px solid #e0e0e0",
            borderRadius: 1,
            overflow: "hidden",
            bgcolor: isSelected
              ? "rgba(206, 149, 15, 0.02)"
              : isHovered
              ? "rgba(245, 230, 163, 0.1)"
              : "white",
            cursor: "default",
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            transform:
              isHovered && !isSelected ? "translateY(-2px)" : "translateY(0)",
            boxShadow: isSelected
              ? "0 4px 12px rgba(206, 149, 15, 0.2)"
              : isHovered
              ? "0 4px 12px rgba(245, 230, 163, 0.3)"
              : "0 2px 4px rgba(0,0,0,0.05)",
            width: "100%",
          }}
        >
          {/* Compact Block Label */}
          <Box
            sx={{
              px: 1.5,
              py: 1,
              bgcolor: isSelected
                ? "#CE950F"
                : isHovered
                ? "#f5e6a3"
                : "#f8f9fa",
              color: isSelected ? "white" : isHovered ? "#1C436A" : "#333",
              borderBottom: "1px solid #e0e0e0",
              transition: "all 0.3s ease",
            }}
          >
            <Typography
              variant="caption"
              fontWeight="bold"
              sx={{ display: "block", lineHeight: 1.2 }}
            >
              {isSelected ? "✓ " : ""}
              {block.name}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                opacity: 0.8,
                display: "block",
                fontSize: "0.7rem",
                lineHeight: 1.1,
                mt: 0.3,
              }}
            >
              {getBlockDescription(block.id)}
            </Typography>
          </Box>

          {/* Compact Block Content */}
          {PreviewComponent ? (
            <Box
              sx={{
                width: "100%",
                overflow: "hidden",
                height: "300px",
                "& > *": {
                  transform: "scale(0.35)",
                  transformOrigin: "top left",
                  width: "285.71% !important", // 100% / 0.35 = 285.71%
                  maxWidth: "285.71% !important",
                  boxSizing: "border-box",
                },
              }}
            >
              <PreviewComponent config={config} />
            </Box>
          ) : (
            <Box
              sx={{
                p: 1.5,
                textAlign: "center",
                fontSize: "0.75rem",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                minHeight: 60,
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ lineHeight: 1.2 }}
              >
                {block.description}
              </Typography>
            </Box>
          )}
        </Box>
      </Grid>
    </Grid>
  );
};

// Remove old RealBlockPreview component as it's now integrated into AlignedBlockRow

export default BlockSelector;
